import { persist, subscribeWithSelector } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';
import type { StateCreator } from 'zustand/vanilla';

import { createDevtools } from '../middleware/create-dev-tools';
import { initialState, type UserState } from './initial-state';
import { createAuthSlice, type UserAuthAction } from './slices/auth/action';
import { type CommonAction, createCommonSlice } from './slices/common/action';
import { createSettingsSlice, type UserSettingsAction } from './slices/settings/action';

//  ===============  Aggregate createStoreFn ============ //

export type UserStore = UserState & UserSettingsAction & UserAuthAction & CommonAction;

const createStore: StateCreator<UserStore, [['zustand/persist', unknown], ['zustand/devtools', never]]> = (...parameters) => ({
  ...initialState,
  ...createSettingsSlice(...parameters),
  ...createAuthSlice(...parameters),
  ...createCommonSlice(...parameters),
});

//  ===============  Implement useStore ============ //

const devtools = createDevtools('user');

export const useUserStore = createWithEqualityFn<UserStore>()(
  subscribeWithSelector(
    persist(devtools(createStore), {
      name: 'rubidex-user-storage',
      partialize: (state) => ({
        // Persist auth state (cookie-based: no token/refreshToken)
        isAuthenticated: state.isAuthenticated,
        currentUser: state.currentUser,
        // Persist private space
        privateSpaceId: state.privateSpaceId,
        isInitialized: state.isInitialized,
        // Persist user settings
        settings: state.settings,
        // Persist compliance flags
        termsAccepted: state.termsAccepted,
        analyticsAccepted: state.analyticsAccepted,
      }),
    }),
  ),
  shallow,
);

export const getUserStoreState = () => useUserStore.getState();
